import { createOccurrence } from '../occurrence'
import { evaluateRecurringTemplate } from '../evaluateRecurringTemplate'
import { getNextOccurrenceDate } from '../getNextOccurrenceDate'

// ============================================================
// GENERATE OCCURRENCES
// ============================================================
//
// The generator walks the recurrence calendar from the
// template's current scheduling position through the
// operational date.
//
// Current scheduling position:
//
//   next_due_date
//       ↓
//   recurrence_start_date (fallback)
//
// The generator does NOT decide whether an occurrence should
// generate a task.
//
// It asks the recurrence evaluators.
//
// ============================================================


// ============================================================
// LOCAL DATE HELPER
// ============================================================

function toLocalDate(value) {

    if (!value) {
        return null
    }

    if (value instanceof Date) {

        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
        )
    }

    // --------------------------------------------------------
    // Date-only database value
    // --------------------------------------------------------

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {

        const [
            year,
            month,
            day
        ] =
            value
                .split('-')
                .map(Number)

        return new Date(
            year,
            month - 1,
            day
        )
    }

    const parsed =
        new Date(value)

    if (
        Number.isNaN(
            parsed.getTime()
        )
    ) {
        return null
    }

    return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
    )
}


// ============================================================
// GENERATE OCCURRENCES
// ============================================================

export function generateOccurrences({

    template,
    existingTasks,
    operationalDate

}) {

    const occurrences = []

    // --------------------------------------------------------
    // Establish the operational date as a local calendar date.
    // --------------------------------------------------------

    const endDate =
        toLocalDate(
            operationalDate
        )

    // --------------------------------------------------------
    // Start from the template's current scheduling position.
    //
    // next_due_date is the engine's current recurrence anchor.
    //
    // If it does not exist yet, fall back to the original
    // recurrence start date.
    // --------------------------------------------------------

    let evaluationDate =
        toLocalDate(
            template.next_due_date
        ) ||
        toLocalDate(
            template.recurrence_start_date
        )

    console.log(
        'Template:',
        template.name
    )

    console.log(
        'Start:',
        template.recurrence_start_date
    )

    console.log(
        'Next due:',
        template.next_due_date
    )

    console.log(
        'Evaluation:',
        evaluationDate
    )

    console.log(
        'Operational:',
        endDate
    )

    // --------------------------------------------------------
    // Safety check
    // --------------------------------------------------------

    if (!evaluationDate) {

        console.warn(
            'Cannot generate occurrences:',
            template.name,
            'has no next_due_date or recurrence_start_date'
        )

        return occurrences
    }

    if (!endDate) {

        console.warn(
            'Cannot generate occurrences:',
            template.name,
            'has no operationalDate'
        )

        return occurrences
    }

    // --------------------------------------------------------
    // Safety limit
    // --------------------------------------------------------

    let iterationCount = 0

    const MAX_ITERATIONS = 5000

    // ========================================================
    // WALK THE RECURRENCE CALENDAR
    // ========================================================

    while (
        evaluationDate &&
        evaluationDate <= endDate &&
        iterationCount < MAX_ITERATIONS
    ) {

        iterationCount++

        console.log(
            'Generating occurrence for',
            template.name,
            evaluationDate
        )

        // ----------------------------------------------------
        // Ask the recurrence evaluator whether this date
        // represents a valid/generated occurrence.
        // ----------------------------------------------------

        const decision =
            evaluateRecurringTemplate({

                template,

                existingTasks,

                operationalDate:
                    evaluationDate

            })

        console.log(

            template.name,

            evaluationDate
                .toISOString()
                .slice(0, 10),

            'generate:',
            decision.generate,

            decision.reason

        )

        // ----------------------------------------------------
        // Store the occurrence decision.
        // ----------------------------------------------------

        occurrences.push(

            createOccurrence({

                template,

                operationalDate:
                    evaluationDate,

                dueDate:
                    decision.dueDate,

                expected:
                    true,

                generate:
                    decision.generate,

                reason:
                    decision.reason,

                code:
                    decision.code

            })
        )

        // ====================================================
        // ADVANCE TO THE NEXT OCCURRENCE
        // ====================================================

        const nextDate =
            getNextOccurrenceDate({

                template,

                currentDate:
                    evaluationDate

            })

        console.log(

            template.name,

            'Advanced to',

            nextDate
                ? nextDate
                    .toISOString()
                    .slice(0, 10)
                : null

        )

        // ----------------------------------------------------
        // No next occurrence.
        // ----------------------------------------------------

        if (!nextDate) {

            console.log(

                template.name,

                'No next occurrence. Stopping.'

            )

            evaluationDate = null

            break
        }

        // ----------------------------------------------------
        // Safety check:
        //
        // The next occurrence must move forward.
        // ----------------------------------------------------

        if (
            nextDate <= evaluationDate
        ) {

            console.error(

                'Recurrence engine failed to advance date for',
                template.name,

                'Current:',
                evaluationDate
                    .toISOString()
                    .slice(0, 10),

                'Next:',
                nextDate
                    .toISOString()
                    .slice(0, 10)

            )

            evaluationDate = null

            break
        }

        evaluationDate =
            nextDate
    }

    // ========================================================
    // SAFETY WARNING
    // ========================================================

    if (
        iterationCount >= MAX_ITERATIONS
    ) {

        console.error(

            'Maximum recurrence iterations reached for',
            template.name,

            'Possible recurrence advancement loop.'

        )
    }

    // ========================================================
    // RETURN OCCURRENCES
    // ========================================================

    return occurrences
}